// Predict-the-Bracket — pure logic for the bracket prediction game.
//
// The FFTBG bracket is 8 sequential matches (indices 0-7, matching the `winners`
// array + map indices in Luxstadio):
//   0 red|blue   1 green|yellow   2 white|black   3 purple|brown   (quarterfinals)
//   4 SF1 = winner(0) vs winner(1)        5 SF2 = winner(2) vs winner(3)
//   6 Final = winner(4) vs winner(5)      7 Champ = winner(6) vs the reigning Champion
//
// A pick for match i is editable until the match resolves (`winners[i]` posts), then
// it locks and grades. Picks cascade: a later match's candidates come from the real
// upstream winners OR, where those aren't decided yet, the player's own upstream picks
// — so the same code serves an upfront full-bracket fill AND mid-tournament "predict
// the rest". Team names are the lowercase labels used by `winners` ('red'…'champion').

export const MATCH_DEFS = [
  { i: 0, round: 'qf', weight: 1, teams: ['red', 'blue'] },
  { i: 1, round: 'qf', weight: 1, teams: ['green', 'yellow'] },
  { i: 2, round: 'qf', weight: 1, teams: ['white', 'black'] },
  { i: 3, round: 'qf', weight: 1, teams: ['purple', 'brown'] },
  { i: 4, round: 'sf', weight: 2, from: [0, 1] },
  { i: 5, round: 'sf', weight: 2, from: [2, 3] },
  { i: 6, round: 'final', weight: 3, from: [4, 5] },
  { i: 7, round: 'champ', weight: 4, from: [6, 'champion'] },
];

export const ROUND_LABEL = { qf: 'Quarterfinal', sf: 'Semifinal', final: 'Final', champ: 'Champ Match' };

// The team occupying an upstream slot: the real winner if decided, else the player's
// pick for that slot. The literal 'champion' slot (champ match) is always the champion.
function effective(slot, winners, picks) {
  if (slot === 'champion') return 'champion';
  return (winners && winners[slot]) || (picks && picks[slot]) || null;
}

// The two candidate teams for match i, from real results ∪ the player's upstream picks.
// An entry is null when its upstream isn't decided or picked yet.
export function matchCandidates(i, winners, picks) {
  const def = MATCH_DEFS[i];
  if (def.teams) return def.teams.slice();
  return def.from.map((slot) => effective(slot, winners, picks));
}

// Can match i be picked right now? Both candidates known and the match not yet decided.
export function isPickable(i, winners, picks) {
  if (winners && winners[i]) return false; // decided → locked
  const [a, b] = matchCandidates(i, winners, picks);
  return !!a && !!b;
}

// Grade one match: null if not gradeable (undecided or no pick), else {correct, points}.
export function gradeMatch(i, picks, winners) {
  const winner = winners && winners[i];
  const pick = picks && picks[i];
  if (!winner || !pick) return null;
  const correct = pick === winner;
  return { correct, points: correct ? MATCH_DEFS[i].weight : 0 };
}

// Full scorecard across the 8 matches.
export function scorePredictions(picks, winners) {
  picks = picks || {}; winners = winners || [];
  let correct = 0, decidedWithPick = 0, points = 0, maxPoints = 0, picksMade = 0;
  const perMatch = MATCH_DEFS.map((def) => {
    const pick = picks[def.i] || null;
    const winner = winners[def.i] || null;
    if (pick) picksMade += 1;
    const g = gradeMatch(def.i, picks, winners);
    if (g) {
      decidedWithPick += 1;
      if (g.correct) correct += 1;
      points += g.points;
      maxPoints += def.weight;
    }
    return { i: def.i, round: def.round, weight: def.weight, pick, winner, decided: !!winner, graded: !!g, correct: g ? g.correct : null };
  });
  return { perMatch, correct, decidedWithPick, points, maxPoints, picksMade };
}

// You-vs-the-sim head-to-head over decided matches where both have a pick.
export function simRecord(picks, simPicks, winners) {
  picks = picks || {}; simPicks = simPicks || {}; winners = winners || [];
  let you = 0, sim = 0, decided = 0;
  for (const def of MATCH_DEFS) {
    const w = winners[def.i];
    if (!w) continue;
    const p = picks[def.i], s = simPicks[def.i];
    if (!p || !s) continue;
    decided += 1;
    if (p === w) you += 1;
    if (s === w) sim += 1;
  }
  return { you, sim, decided, beat: you > sim, tied: you === sim };
}

// Drop picks whose team is no longer a candidate for that match (an upstream result or
// re-pick busted them). Single pass in match order handles the cascade (4,5 → 6 → 7).
export function pruneBusted(picks, winners) {
  for (const def of MATCH_DEFS) {
    if (def.teams) continue; // QF candidates are fixed, never bust
    const pick = picks[def.i];
    if (!pick) continue;
    const [a, b] = matchCandidates(def.i, winners, picks);
    if (pick !== a && pick !== b) delete picks[def.i];
  }
}

// ---- localStorage (versioned, fails silent like userPrefs) ----

export const STORAGE_KEY = 'wiznaibus.predictions.v1';

function emptyState() {
  return {
    v: 1,
    byTournament: {},
    lifetime: { tournaments: 0, correct: 0, total: 0, points: 0, beatSim: 0, lostSim: 0, tieSim: 0, streak: 0, bestStreak: 0 },
  };
}

export function loadPredictions() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyState();
    const obj = JSON.parse(raw);
    if (!obj || obj.v !== 1) return emptyState();
    const base = emptyState();
    return { ...base, ...obj, lifetime: { ...base.lifetime, ...(obj.lifetime || {}) }, byTournament: obj.byTournament || {} };
  } catch {
    return emptyState();
  }
}

export function savePredictions(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* private mode / quota */ }
}

// Get (creating if absent) the prediction record for a tournament id.
// `active` = the player turned predict mode on for this bracket (opted in).
export function tournamentRecord(state, id) {
  if (!state.byTournament[id]) state.byTournament[id] = { picks: {}, sim: {}, active: false, finalized: false };
  const rec = state.byTournament[id];
  if (!rec.picks) rec.picks = {};
  if (!rec.sim) rec.sim = {};
  return rec;
}

// Has the player opted into (turned predict mode on for) this tournament?
export function isActive(state, id) {
  const rec = id && state.byTournament && state.byTournament[id];
  return !!(rec && rec.active);
}

// Roll a completed tournament's result into the lifetime record. Idempotent:
// no-op if already finalized, the player wasn't active, or made no picks. `winners`
// must be the full result. Mutates state.lifetime; returns true if it updated.
export function finalizeTournament(state, id, winners) {
  const rec = state.byTournament && state.byTournament[id];
  if (!rec || rec.finalized || !rec.active) return false;
  const score = scorePredictions(rec.picks, winners);
  if (!score.picksMade) return false;
  const sim = simRecord(rec.picks, rec.sim, winners);
  const L = state.lifetime;
  L.tournaments += 1;
  L.correct += score.correct;
  L.total += score.decidedWithPick;
  L.points += score.points;
  if (sim.decided > 0) {
    if (sim.you > sim.sim) { L.beatSim += 1; L.streak += 1; L.bestStreak = Math.max(L.bestStreak, L.streak); }
    else if (sim.you < sim.sim) { L.lostSim += 1; L.streak = 0; }
    else { L.tieSim += 1; L.streak = 0; } // a tie doesn't continue a beat-the-sim streak
  }
  rec.finalized = true;
  return true;
}

// One-line shareable summary of a finished (or in-progress) bracket.
export function shareText(score, sim) {
  let s = `🔮 My FFTBG bracket: ${score.correct}/${score.decidedWithPick} right, ${score.points} pts`;
  if (sim && sim.decided > 0) {
    const verb = sim.you > sim.sim ? 'beat' : (sim.you < sim.sim ? 'lost to' : 'tied');
    s += ` — ${verb} the sim ${sim.you}–${sim.sim} 🤖`;
  }
  return s + '. Predict yours at wiznaib.us';
}

// Set a pick for match i, then prune any now-busted downstream picks. Mutates rec.
export function setPick(rec, i, team, winners) {
  if (team == null) delete rec.picks[i];
  else rec.picks[i] = team;
  pruneBusted(rec.picks, winners);
  return rec;
}

// ---- share encoding: 8 chars, one team-code per match ('-' = no pick) ----

const CODE = { red: 'R', blue: 'B', green: 'G', yellow: 'Y', white: 'W', black: 'K', purple: 'P', brown: 'N', champion: 'C' };
const DECODE = Object.fromEntries(Object.entries(CODE).map(([k, v]) => [v, k]));

export function encodePicks(picks) {
  picks = picks || {};
  let s = '';
  for (let i = 0; i < 8; i += 1) s += picks[i] ? (CODE[picks[i]] || '-') : '-';
  return s;
}

export function decodePicks(str) {
  const picks = {};
  if (!str) return picks;
  for (let i = 0; i < 8 && i < str.length; i += 1) {
    const t = DECODE[str[i]];
    if (t) picks[i] = t;
  }
  return picks;
}
